import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ClientUserGroupBase, DeleteUserGroupResponseWorkflow } from "../../../../api/types/userManagementTypes";
import UserGroupCannotBeDeletedDialog from "./UserGroupCannotBeDeletedDialog";

interface Props {
  userGroup: ClientUserGroupBase;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

interface CannotDeleteDialogState {
  userGroupName: string;
  workflows: DeleteUserGroupResponseWorkflow[];
}

const deleteClientUserGroup = withErrorHandling(adminApi.deleteClientUserGroup);

const DeleteUserGroupDialog = ({ userGroup, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [cannotDeleteDialogState, setCannotDeleteDialogState] = useState<CannotDeleteDialogState>();

  const handleDelete = async () => {
    setLoading(true);
    const [resp, error] = await deleteClientUserGroup(userGroup.id);
    setLoading(false);

    if (error) {
      logError(error, "[DeleteUserGroupDialog] deleteClientUserGroup");
      sendNotificationError(error.message);
      return;
    }

    if (resp.isDeleted) {
      sendNotification("User group deleted successfully");
      onDeleted(userGroup.id);
      onClose();
      return;
    }

    setCannotDeleteDialogState({
      userGroupName: userGroup.name,
      workflows: resp.usedInWorkflows,
    });
  };

  if (cannotDeleteDialogState) {
    return (
      <UserGroupCannotBeDeletedDialog
        userGroupName={cannotDeleteDialogState.userGroupName}
        workflows={cannotDeleteDialogState.workflows}
        onClose={onClose}
      />
    );
  }

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete User Group</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete {userGroup.name}?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete this user group. This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteUserGroupDialog;
